import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Button, cx } from "@/components/ui";
import { t } from "@/lib/i18n";

export interface PickableColumn<K extends string> {
  key: K;
  /** mit msg() markiert, wird hier übersetzt */
  label: string;
  group: string;
}

/** Spalten ein-/ausblenden, nach Gruppen – je Browser gemerkt (siehe useColumnChoice) */
export function ColumnPicker<K extends string>({ columns, value, onChange, defaults }: { columns: PickableColumn<K>[]; value: K[]; onChange: (v: K[]) => void; defaults: K[] }) {
  const groups = [...new Set(columns.map((c) => c.group))];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button size="sm" variant="ghost" icon={<SlidersHorizontal className="size-3.5" />}>{t("Spalten")}</Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-64 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-surface p-1.5 shadow-xl animate-pop-in">
          {groups.map((g) => (
            <div key={g}>
              <DropdownMenu.Label className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">{t(g)}</DropdownMenu.Label>
              {columns.filter((c) => c.group === g).map((c) => {
                const on = value.includes(c.key);
                return (
                  <DropdownMenu.CheckboxItem
                    key={c.key}
                    checked={on}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(v) => onChange(v ? [...value, c.key] : value.filter((x) => x !== c.key))}
                    className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2"
                  >
                    <span className={cx("size-4 grid place-items-center rounded border", on ? "bg-ink border-ink text-ink-fg" : "border-border")}>{on && <Check className="size-3" />}</span>
                    {t(c.label)}
                  </DropdownMenu.CheckboxItem>
                );
              })}
            </div>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item onSelect={() => onChange(defaults)} className="rounded-lg px-2 py-1.5 text-[12.5px] text-fg-muted outline-none cursor-pointer data-[highlighted]:bg-surface-2">{t("Standard wiederherstellen")}</DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Standardspalten, die nach Einführung der Spaltenwahl dazukamen (für ältere Speicherungen ohne Liste) */
const NEW_DEFAULT_COLUMNS = new Set<string>(["warehouse"]);

/**
 * gewählte Spalten je Browser merken; unbekannte (z. B. entfernte) Schlüssel fallen weg. Neue Standardspalten, die es
 * beim Speichern noch nicht gab, kommen automatisch dazu (sonst sähe sie niemand, der seine Spalten angepasst hat).
 */
export function useColumnChoice<K extends string>(storageKey: string, all: readonly K[], defaults: K[]): [K[], (v: K[]) => void] {
  const knownKey = `${storageKey}:known`;
  const [value, setValue] = useState<K[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(storageKey) ?? "null") as K[] | null;
      if (!Array.isArray(v)) return defaults;
      const known = JSON.parse(localStorage.getItem(knownKey) ?? "null") as K[] | null;
      // ohne Liste (ältere Speicherung): als „kannte alles außer neuen Standardspalten“ werten
      const fresh = defaults.filter((k) => !v.includes(k) && (Array.isArray(known) ? !known.includes(k) : NEW_DEFAULT_COLUMNS.has(k)));
      return [...v.filter((k) => all.includes(k)), ...fresh];
    } catch {
      return defaults;
    }
  });
  const set = (v: K[]) => {
    setValue(v);
    try {
      localStorage.setItem(storageKey, JSON.stringify(v));
      localStorage.setItem(knownKey, JSON.stringify(all));
    } catch {
      /* privates Fenster – gilt dann bis zum Neuladen */
    }
  };
  return [value, set];
}
